// Counterpart of webapp/src/store/initialLoad.ts — the boot sequence.
//
// Upstream: fetch clientConfig -> fetch me -> load teams -> loadWorkspaceData
// (all boards, then each board's cards/views) -> load sidebar categories.

import {useBoards} from './boards'
import {useCards} from './cards'
import {useViews} from './views'
import {useComments} from './comments'
import {useUsers} from './users'
import {useTeams} from './teams'
import {useSidebar} from './sidebar'
import {useClientConfig} from './clientConfig'
import {initThemes} from '../common/theme'
import {Constants} from '../common/constants'

/** Everything the sidebar and the board route need for the active team. */
export async function loadWorkspaceData(teamId: string): Promise<void> {
    const boards = await useBoards.getState().loadBoards(teamId)
    await Promise.all(boards.map(async (board) => {
        await Promise.all([
            useCards.getState().loadCards(board.id),
            useViews.getState().loadViews(board.id),
        ])
    }))
    await Promise.all([
        useSidebar.getState().loadCategories(teamId),
        useUsers.getState().fetchUsers(teamId),
    ])
}

/** Refresh a single board (used by the board route when data may be stale). */
export async function loadBoardData(boardId: string): Promise<void> {
    await Promise.all([
        useBoards.getState().loadBoard(boardId),
        useCards.getState().loadCards(boardId),
        useViews.getState().loadViews(boardId),
        useComments.getState().loadBoardComments(boardId),
    ])
}

export type BootstrapResult = {
    loggedIn: boolean
    teamId: string
}

export async function bootstrap(): Promise<BootstrapResult> {
    initThemes()
    await useClientConfig.getState().loadClientConfig()

    let me = await useUsers.getState().fetchMe()

    // No session yet: sign in as the demo user so the app opens directly on a
    // board. The mock server still issues and validates a real bearer token.
    if (!me) {
        const ok = await useUsers.getState().login(Constants.demoAccount.username, Constants.demoAccount.password)
        if (!ok) {
            return {loggedIn: false, teamId: ''}
        }
        me = useUsers.getState().me ?? undefined
    }

    const teams = await useTeams.getState().loadTeams()
    const teamId = useTeams.getState().activeTeamId || teams[0]?.id
    if (teamId) {
        await loadWorkspaceData(teamId)
    }
    return {loggedIn: Boolean(teamId), teamId: teamId || ''}
}

/** After a successful login: same path, minus the "am I already logged in" probe. */
export async function onLoggedIn(): Promise<string> {
    const teams = await useTeams.getState().loadTeams()
    const teamId = teams[0]?.id || ''
    useTeams.getState().setTeamId(teamId)
    await loadWorkspaceData(teamId)
    return teamId
}
